import { useReducer, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { setState } from '@chronos/ui-toolkit/actions';
import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import {
  initialPermissionsState,
  useGetTrelloBoardMemberPermissionsQuery,
} from '@chronos/slices/settingsApi';
import { useBoardContext } from '@chronos/ui-toolkit/contexts/index';
import Modal from '../Modal';
import * as S from './styled';

const initialState = {
  comment: '',
  errors: {
    comment: '',
  },
};

const WorklogCommentForm = (props) => {
  const { memberId } = useBoardContext();
  const { boardId, worklog } = props;
  const {
    data: { entities: boardMemberPermissions } = initialPermissionsState,
    isSuccess: trelloBoardMemberPermissionsLoaded,
  } = useGetTrelloBoardMemberPermissionsQuery({
    boardId,
  });
  const userPermissions = useMemo(() => {
    return (
      Object.values(boardMemberPermissions).find(
        (permission) => permission.accountId === memberId
      ) || { isCommentRequired: false }
    );
  }, [memberId, trelloBoardMemberPermissionsLoaded, boardMemberPermissions]);

  const [state, dispatch] = useReducer(keyValueReducer, initialState);

  useEffect(() => {
    dispatch(
      setState({
        comment: worklog?.comment || '',
      })
    );
  }, [props.isOpen]);

  const validate = useCallback(() => {
    let valid = true;

    const isCommentRequiredError =
      userPermissions.isCommentRequired && !state.comment.length;

    if (isCommentRequiredError) {
      valid = false;
    }
    dispatch(
      setState({
        errors: {
          comment: isCommentRequiredError
            ? 'Work log comment is required for you'
            : '',
          _merge: true,
        },
      })
    );

    return valid;
  }, [state.timeSpent, state.comment]);

  const handleCommentChange = useCallback((e) => {
    dispatch(
      setState({
        comment: e.target.value,
        errors: { comment: '' },
      })
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    const isValid = validate();
    if (!isValid) return;

    try {
      dispatch(setState({ saveInProcess: true }));
      await props.handleSubmit(state.comment);
      dispatch(setState({ saveInProcess: false }));
      props.handleClose(true);
    } catch (err) {
      dispatch(setState({ saveInProcess: false }));
      Sentry.captureException(err);
    }
  }, [state]);

  const moveCaretAtEnd = useCallback((e) => {
    const { value } = e.target;
    e.target.value = '';
    e.target.value = value;
  }, []);

  return (
    <Modal
      isOpen={props.isOpen}
      isMobile={props.isMobile}
      isSaving={state.saveInProcess}
      handleClose={props.handleClose}
      handleSubmit={handleSubmit}
      title={props.title}
    >
      <S.FieldWrapper>
        <S.WorklogComment>
          <S.InputLabel>
            Worklog comment
            {userPermissions.isCommentRequired && (
              <S.RequiredIndicator>*</S.RequiredIndicator>
            )}
          </S.InputLabel>
          <S.Textarea
            autoFocus
            onFocus={moveCaretAtEnd}
            minRows={4}
            maxRows={10}
            style={{ width: '100%' }}
            value={state.comment || ''}
            isInvalid={state.errors.comment !== ''}
            onChange={handleCommentChange}
          />
        </S.WorklogComment>
      </S.FieldWrapper>
    </Modal>
  );
};

WorklogCommentForm.defaultProps = {
  isOpen: false,
  isMobile: false,
  worklog: null,
};

WorklogCommentForm.propTypes = {
  title: PropTypes.string.isRequired,
  boardId: PropTypes.string.isRequired,
  worklog: PropTypes.shape({
    id: PropTypes.string,
    startedAt: PropTypes.string,
    seconds: PropTypes.number,
    comment: PropTypes.string,
  }),
  isOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
};

export default WorklogCommentForm;
